import { ChartType, AppType } from "./global-type";
import { ColumnType, ITableData } from "./table-type";

export enum StatisticsType {
  // 计算总数
  COUNT = "COUNT",
  // 基于指定列进行计算
  STATISTIC_COLUMN = "STATISTIC_COLUMN",
}

// value 统计方式
export enum CalculationType {
  // 求和
  SUM = "SUM",
  // 最大值
  MAX = "MAX",
  // 最小值
  MIN = "MIN",
  // 平均数
  AVERAGE = "AVERAGE",
}

// 维度 选择 time column 时 可以基于周期进行 group by
export enum TimeCycleType {
  DAY = "DAY",
  WEEK = "WEEK",
  MONTH = "MONTH",
  QUARTER = "QUARTER",
  YEAR = "YEAR",
}

// 图表内容排序规则类型
export enum ContentSortType {
  X_FIRST_TO_LAST = "X_FIRST_TO_LAST",
  X_LASTT_OF_IRST = "X_LASTT_OF_IRST",
  Y_FIRST_TO_LAST = "Y_FIRST_TO_LAST",
  Y_LASTT_OF_IRST = "Y_LASTT_OF_IRST",
}

// 维度 option 优先级
export enum DimensionColumnPriority {
  RECOMMEND = "RECOMMEND",
  OTHER = "OTHER",
}

// 统计 计算方式 定义
export enum AggregateFunctions {
  // 平均数
  AVERAGE = "AVERAGE",
  CHECKED = "CHECKED",
  COUNT = "COUNT",
  DATE_RANGE_DAYS = "DATE_RANGE_DAYS",
  DATE_RANGE_MONTH = "DATE_RANGE_MONTH",
  EARLIEST_DATE = "EARLIEST_DATE",
  EMPTY = "EMPTY",
  FILLED = "FILLED",
  LATEST_DATE = "LATEST_DATE",
  MAX = "MAX",
  // 中位数
  MEDIAN = "MEDIAN",
  MIN = "MIN",
  PERCENTAGE_CHECKED = "PERCENTAGE_CHECKED",
  PERCENTAGE_EMPTY = "PERCENTAGE_EMPTY",
  PERCENTAGE_FILLED = "PERCENTAGE_FILLED",
  PERCENTAGE_UNCHECKED = "PERCENTAGE_UNCHECKED",
  PERCENTAGE_UNIQUE = "PERCENTAGE_UNIQUE",
  // 范围
  RANGE = "RANGE",
  // 标准差
  STANDARD_DEVIATION = "STANDARD_DEVIATION",
  SUM = "SUM",
  UNCHECKED = "UNCHECKED",
  UNIQUE = "UNIQUE",
}

export type IFormValue<T extends AppType = AppType> = T extends AppType.CHART
  ? IChartFormValue
  : T extends AppType.AGGREGATE
  ? IAggregateFormValue
  : never;

export interface IAggregateFormValue {
  // 统计方式
  statisticsType: StatisticsType;
  // 计算方式
  calculationType: AggregateFunctions;
  // 计算目标列 id
  calculationTargetId?: string;
  // 筛选面板状态
  filterFold: boolean;
  filterMeta: IFilterMeta;
}

export interface IChartFormValue {
  // ----------------- chart type -----------------
  chartType: ChartType;
  // ----------------- chart type -----------------

  // ----------------- 维度 设置 -----------------
  // 用于 维度 的列 id
  dimensionId: string;
  // 是否按周期合并
  isGroupAtCycle?: boolean;
  // 按周期合并
  groupAtCycle: TimeCycleType;
  // 显示 维度 空值
  isShowDimensionNull: boolean;
  // 拆分计算
  isSplit?: boolean;
  // ----------------- 维度 设置 -----------------

  // ----------------- value 设置 -----------------
  // 统计方式
  statisticsType: StatisticsType;
  // 计算方式
  calculationType: CalculationType;
  // 计算目标列 id
  calculationTargetId?: string;
  // ----------------- value 设置 -----------------

  // ----------------- 分组 or 堆叠 -----------------
  // 基于以下列堆叠 or 分组
  stackByColumnId?: string;
  // ----------------- 分组 or 堆叠 -----------------

  // ----------------- 更多设置 -----------------
  moreFold: boolean;
  // 图表显示排序
  contentSort: ContentSortType;
  // 显示图例
  isShowLegend: boolean;
  // 显示y轴
  isShowYAxis?: boolean;
  // 显示数值标签
  isShowLabel: boolean;
  // 平滑曲线
  isSmoothLine?: boolean;
  // 使用 option 中的 color 用于渲染图表
  useOptionColor?: boolean;
  // ----------------- 更多设置 -----------------

  // ----------------- 筛选 -----------------
  filterFold: boolean;
  filterMeta: IFilterMeta;
  // ----------------- 筛选 -----------------
}

export enum IFormComponentType {
  ChartTypeSelect = "ChartTypeSelect",
  Input = "Input",
  Select = "Select",
  Switch = "Switch",
  Title = "Title",
  SelectForTree = "SelectForTree",
  SelectForTab = "SelectForTab",
  FormBlock = "FormBlock",
  Filter = "Filter",
}

export enum FilterType {
  ATTACHMENT = "ATTACHMENT",
  CHECKBOX = "CHECKBOX",
  COLLABORATOR = "COLLABORATOR",
  CREATED_BY = "CREATED_BY",
  DATE = "DATE",
  EMAIL = "EMAIL",
  LOOKUP = "LOOKUP",
  MULTI_SELECT = "MULTI_SELECT",
  NUMBER = "NUMBER",
  PHONE = "PHONE",
  RECORD_REFERENCE = "RECORD_REFERENCE",
  SELECT = "SELECT",
  STATUS = "STATUS",
  TEXT = "TEXT",
}

// 比较条件
export enum FilterCondition {
  CONTAINS = "CONTAINS",
  ENDS_WITH = "ENDS_WITH",
  EQUALS = "EQUALS",
  EQUALS_ANY_OF = "EQUALS_ANY_OF",
  EQUALS_NONE_OF = "EQUALS_NONE_OF",
  FILE_NAME = "FILE_NAME",
  FILE_TYPE = "FILE_TYPE",
  GREATER_THAN = "GREATER_THAN",
  GREATER_THAN_OR_EQUAL = "GREATER_THAN_OR_EQUAL",
  HAS_ALL_OF = "HAS_ALL_OF",
  HAS_ANY_OF = "HAS_ANY_OF",
  HAS_NONE_OF = "HAS_NONE_OF",
  INTERSECTS = "INTERSECTS",
  IS_EMPTY = "IS_EMPTY",
  IS_NOT_EMPTY = "IS_NOT_EMPTY",
  LESS_THAN = "LESS_THAN",
  LESS_THAN_OR_EQUAL = "LESS_THAN_OR_EQUAL",
  NOT_CONTAINS = "NOT_CONTAINS",
  NOT_EQUAL = "NOT_EQUAL",
  STARTS_WITH = "STARTS_WITH",
}

export enum DateComparisonOptions {
  EXACT_DATE = "EXACT_DATE",
  NUM_DAYS_AGO = "NUM_DAYS_AGO",
  NUM_DAYS_FROM_NOW = "NUM_DAYS_FROM_NOW",
  ONE_MONTH_AGO = "ONE_MONTH_AGO",
  ONE_MONTH_FROM_NOW = "ONE_MONTH_FROM_NOW",
  ONE_WEEK_AGO = "ONE_WEEK_AGO",
  ONE_WEEK_FROM_NOW = "ONE_WEEK_FROM_NOW",
  TODAY = "TODAY",
  TOMORROW = "TOMORROW",
  YESTERDAY = "YESTERDAY",
}

export enum AttachmentFileTypeOptions {
  EXCEL = "EXCEL",
  IMAGE = "IMAGE",
  PDF = "PDF",
  TEXT = "TEXT",
}

export const FILE_TYPE_FILTERS_EXTENSIONS: { [key in AttachmentFileTypeOptions]: string[] } = {
  [AttachmentFileTypeOptions.TEXT]: [".txt", ".doc", ".docx"],
  [AttachmentFileTypeOptions.IMAGE]: [".jpeg", ".png", ".gif", ".jpg", ".jpeg", ".bmp", ".svg"],
  [AttachmentFileTypeOptions.PDF]: [".pdf"],
  [AttachmentFileTypeOptions.EXCEL]: [".xls", ".xlsx"],
};

export enum OperatorType {
  AND = "AND",
  OR = "OR",
}

export interface IFormSelectOption {
  name: string;
  value: string;
}

export interface IFormConfigOption {
  componentType: IFormComponentType;
  labelId?: string;
  id: string;
  options?: Object[];
  defaultValue?: string;
  placeholderId?: string;
  isShow?: (value: IFormValue) => boolean;
  validates?: {
    checkFunc: (data: any) => boolean;
    errorMessageId: string;
  }[];
  getOptions?: (formValue: IFormValue, tableData: ITableData) => Object[];
}

export interface IFormBlockOption {
  componentType: IFormComponentType;
  // componentType: IFormComponentType.FormBlock;
  id: string;
  titleId?: string;
  childrenOption: IFormConfigOption[];
  // 是否允许展开收起
  allowFolding: boolean;
}

export interface IFormItemChangeValue {
  [id: string]: string | boolean;
}

export interface IDimensionSelectOption {
  name: string;
  value: string;
  priorityLevel: DimensionColumnPriority;
  priorityLevelNumber: number;
  priorityLevelLabelId: string;
  type?: ColumnType;
}

export interface IValueSelectOption {
  name: string;
  value: string;
  isFormattedMessage?: boolean;
}

export interface IFilterValue {
  columnId: string;
  filterType: FilterType;
  filterCondition: FilterCondition;
  dateComparisonOptions?: DateComparisonOptions | null;
  attachmentFileTypeOptions?: AttachmentFileTypeOptions | null;
  filterString?: any | null;
  // 暂时不用 id  如需使用id 则用 uuid 生成
  // id: string;
  // filterByMe 不实现该字段
}

export interface IFilterMeta {
  filterValues: IFilterValue[];
  operatorType: OperatorType;
}

export interface IFilterCtrl {
  columnId: string;
  dateComparisonOptions?: IFilterValue["dateComparisonOptions"];
  attachmentFileTypeOptions?: IFilterValue["attachmentFileTypeOptions"];
  filterType: FilterType;
  filterCondition: FilterCondition;
  filterString: string;
  id: string;
}

export interface IAdaptedFilterCtrl extends IFilterCtrl {
  operatorType: OperatorType;
}

export enum SummaryFormatValueTagType {
  // 标准高度
  STANDARD_HEIGHT = "STANDARD_HEIGHT",
  // 二分之一高度
  ONE_HALF_HEIGHT = "ONE_HALF_HEIGHT",
  // 三分之一高度
  ONE_THIRD_HEIGHT = "ONE_THIRD_HEIGHT",
}
export interface SummaryFormatValueItem {
  value: string;
  tagType: SummaryFormatValueTagType;
}
